// client/src/pages/tabs/Edgemarc-tab/hooks/useEdgemarc.js
import { useState, useEffect } from 'react';
import { customersApiService } from '../../../services';

const useEdgemarc = id => {
  const [, setTicket] = useState(null);
  const [formData, setFormData] = useState({
    phone1IpAddress: '',
    phone2IpAddress: '',
    phone3IpAddress: '',
  });

  const [inputs, setInputs] = useState({
    inputSecureCrtWan1: { value: '-- WAN1 -- - TKT# -', label: 'SSH for WAN1' },
    inputSecureCrtWan2: { value: '-- WAN2 -- - TKT# -', label: 'SSH for WAN2' },
    inputWhoIsWan1: { value: 'whois | grep Orga', label: 'Who Is WAN1' },
    inputWhoIsWan2: { value: 'whois | grep Orga', label: 'Who Is WAN2' },
    inputTcpDumpPhone1: {
      value: 'tcpdump -s0 -i eth0.XXX -vv host',
      label: 'TCPDUMP Command',
    },
    inputTcpDumpPhone2: {
      value: 'tcpdump -s0 -i eth0.XXX -vv host',
      label: 'TCPDUMP Command',
    },
    inputTcpDumpPhone3: {
      value: 'tcpdump -s0 -i eth0.XXX -vv host',
      label: 'TCPDUMP Command',
    },
  });

  const [customerData, setCustomerData] = useState(null);
  const [lanIpEnabled, setLanIpEnabled] = useState({
    phone1: false,
    phone2: false,
    phone3: false,
  });

  useEffect(() => {
    if (id) {
      // Fetch ticket data from Customers API and set document title
      customersApiService.getTicketById(id)
        .then(async data => {
          setTicket(data);

          // Fetch customer from Customers API using customer_number from ticket
          let customerName = data.customer_name;
          try {
            const customer = await customersApiService.getCustomerByNumber(data.customer_number);
            if (customer && customer.customer_name) {
              customerName = customer.customer_name;
            }
          } catch (err) {
            console.warn('Could not fetch fresh customer data, using cached name:', err);
          }

          document.title = `${data.ticket_number} - ${customerName} - ${data.issue_with}`;

          // Fetch full customer data for Edgemarc fields
          try {
            const customer = await customersApiService.getCustomerByNumber(data.customer_number);
            if (customer) {
              setCustomerData(customer);
              setInputs(prev => ({
                ...prev,
                inputSecureCrtWan1: {
                  value: `${customer.customer_number} -- WAN1 -- ${customer.wan1} -- ${customer.customer_name}`,
                  label: 'SSH for WAN1',
                },
                inputSecureCrtWan2: {
                  value: `${customer.customer_number} -- WAN2 -- ${customer.wan2} -- ${customer.customer_name}`,
                  label: 'SSH for WAN2',
                },
                inputWhoIsWan1: {
                  value: `whois ${customer.wan1} | grep Orga`,
                  label: 'Who Is WAN1',
                },
                inputWhoIsWan2: {
                  value: `whois ${customer.wan2} | grep Orga`,
                  label: 'Who Is WAN2',
                },
                inputTcpDumpPhone1: {
                  value: `tcpdump -s0 -i eth0.${customer.eth} -vv host`,
                  label: 'TCPDUMP Command',
                },
                inputTcpDumpPhone2: {
                  value: `tcpdump -s0 -i eth0.${customer.eth} -vv host`,
                  label: 'TCPDUMP Command',
                },
                inputTcpDumpPhone3: {
                  value: `tcpdump -s0 -i eth0.${customer.eth} -vv host`,
                  label: 'TCPDUMP Command',
                },
              }));
            }
          } catch (err) {
            console.error('Error fetching customer for Edgemarc data:', err);
          }
        })
        .catch(err => {
          console.error('Error fetching ticket:', err);
          document.title = 'SSK - Support Swiss Knife';
        });
    }
  }, [id]);

  const handleInputChange = e => {
    const { id, value } = e.target;
    const phoneNumber = id.replace('phone', '').replace('IpAddress', '');
    const isLanIpEnabled = lanIpEnabled[`phone${phoneNumber}`];

    let processedValue = value;
    if (isLanIpEnabled) {
      if (value.startsWith('192.168.1.')) {
        processedValue = `192.168.1.${value.split('.')[3] || 'xxx'}`;
      } else if (!value) {
        processedValue = '192.168.1.xxx';
      }
    }

    setFormData(prev => {
      const newFormData = { ...prev, [id]: processedValue };

      if (id.includes('phone') && customerData) {
        const commandKey = `inputTcpDumpPhone${phoneNumber}`;
        const baseCommand = `tcpdump -s0 -i eth0.${customerData.eth} -vv host`;

        setInputs(prevInputs => ({
          ...prevInputs,
          [commandKey]: {
            ...prevInputs[commandKey],
            value: processedValue
              ? `${baseCommand} ${processedValue}`
              : baseCommand,
          },
        }));
      }

      return newFormData;
    });
  };

  const toggleLanIp = phoneNumber => {
    const phoneKey = `phone${phoneNumber}`;
    const isEnabled = !lanIpEnabled[phoneKey];

    setLanIpEnabled(prev => ({
      ...prev,
      [phoneKey]: isEnabled,
    }));

    setFormData(prev => {
      const newValue = isEnabled ? '192.168.1.xxx' : '';
      const newFormData = { ...prev, [`${phoneKey}IpAddress`]: newValue };

      if (customerData) {
        const commandKey = `inputTcpDumpPhone${phoneNumber}`;
        const baseCommand = `tcpdump -s0 -i eth0.${customerData.eth} -vv host`;

        setInputs(prevInputs => ({
          ...prevInputs,
          [commandKey]: {
            ...prevInputs[commandKey],
            value: newValue ? `${baseCommand} ${newValue}` : baseCommand,
          },
        }));
      }

      return newFormData;
    });
  };

  const copyToClipboard = (id, text) => {
    const contentToCopy = text || inputs[id]?.value;
    if (!contentToCopy) return;

    navigator.clipboard.writeText(contentToCopy).then(() => {
      const messageElement = document.getElementById(`${id}-message`);
      if (messageElement) {
        messageElement.textContent = 'Copied to clipboard!';
        messageElement.classList.remove('hidden');
        setTimeout(() => {
          messageElement.textContent = '';
          messageElement.classList.add('hidden');
        }, 2000);
      }
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  return {
    formData,
    inputs,
    handleInputChange,
    copyToClipboard,
    lanIpEnabled,
    toggleLanIp,
  };
};

export default useEdgemarc;